<script setup lang="ts">
import type { CookieItem } from '~/types/content/cookieItem.type';

const cookies: Record<string, CookieItem[]> = {
    necessary: [
        {
            name: '_GRECAPTCHA',
            provider: 'google.com',
            purpose:
                'Этот файл cookie используется для различения людей и роботов. Это полезно для веб-сайта с целью создания достоверных отчетов об использовании сайта.',
            expiry: '180 дней',
            type: 'HTTP Cookie',
        },
        {
            name: 'CookieConsent',
            provider: 'consent.cookiebot.com',
            purpose: 'Сохраняет состояние согласия пользователя на использование файлов cookie для текущего домена',
            expiry: '1 год',
            type: 'HTTP Cookie',
        },
        {
            name: 'iconify-count',
            provider: 'fleequid.com',
            purpose: 'Используется системой управления контентом (CMS) веб-сайта для определения способа отображения вкладочных меню сайта.',
            expiry: 'Постоянный',
            type: 'HTML Local Storage',
        },
        {
            name: 'iconify-version',
            provider: 'fleequid.com',
            purpose: 'Используется системой управления контентом (CMS) веб-сайта для определения способа отображения вкладочных меню сайта.',
            expiry: 'Постоянный',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::a',
            provider: 'gstatic.com',
            purpose:
                'Этот файл cookie используется для различения людей и роботов. Это полезно для веб-сайта с целью создания достоверных отчетов об использовании сайта.',
            expiry: 'Постоянный',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::b',
            provider: 'gstatic.com',
            purpose: 'Этот файл cookie используется для различения людей и роботов.',
            expiry: 'Постоянный',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::c',
            provider: 'gstatic.com',
            purpose: 'Этот файл cookie используется для различения людей и роботов.',
            expiry: 'Постоянный',
            type: 'HTML Local Storage',
        },
        {
            name: 'rc::f',
            provider: 'gstatic.com',
            purpose: 'Этот файл cookie используется для различения людей и роботов.',
            expiry: 'Постоянный',
            type: 'HTML Local Storage',
        },
    ],
    preferences: [
        {
            name: 'i18n_redirected',
            provider: 'fleequid.com',
            purpose:
                'Определяет предпочтительный язык посетителя. Позволяет веб-сайту устанавливать предпочтительный язык при возвращении посетителя.',
            expiry: '1 год',
            type: 'HTTP Cookie',
        },
    ],
    marketing: [
        {
            name: '_ga',
            provider: 'www.googletagmanager.com',
            purpose:
                'Используется для отправки данных в Google Analytics об устройстве и поведении пользователя. Отслеживает пользователя на разных устройствах и маркетинговых каналах.',
            expiry: '2 года',
            type: 'HTTP Cookie',
        },
        {
            name: '_ga#',
            provider: 'www.googletagmanager.com',
            purpose:
                'Используется для отправки данных в Google Analytics об устройстве и поведении пользователя. Отслеживает пользователя на разных устройствах и маркетинговых каналах.',
            expiry: '2 года',
            type: 'HTTP Cookie',
        },
    ],
    unclassified: [
        {
            name: 'nuxt-color-mode',
            provider: 'fleequid.com',
            purpose: 'Определяет предпочтительную тему посетителя.',
            expiry: 'Постоянный',
            type: 'HTML Local Storage',
        },
    ],
};
</script>
<template>
    <ContentPage title="Политика использования файлов cookie">
        <p>
            Мы используем файлы cookie для персонализации контента и рекламы, предоставления функций социальных сетей и анализа нашего трафика. Мы
            также делимся информацией об использовании вами нашего сайта с нашими партнерами по веб-аналитике, рекламе и социальным сетям, которые
            могут объединять её с другой информацией.
        </p>
        <p>Файлы cookie — это небольшие текстовые файлы, которые веб-сайты могут использовать для повышения эффективности работы пользователя.</p>
        <p>
            Закон гласит, что мы можем хранить файлы cookie на вашем устройстве, если они строго необходимы для работы этого сайта. Для всех других
            типов файлов cookie нам требуется ваше согласие.
        </p>
        <p>
            Этот сайт использует различные типы файлов cookie. Некоторые файлы cookie размещаются сторонними сервисами, которые появляются на наших
            страницах.
        </p>
        <p>Вы можете изменить или отозвать свое согласие в любое время через Заявление о файлах cookie на нашем веб-сайте.</p>
        <p>
            Узнайте больше о том, кто мы, как вы можете связаться с нами и как мы обрабатываем персональные данные в нашей
            <NuxtLinkLocale to="privacy-policy" class="text-primary-500 hover:underline">Политике конфиденциальности</NuxtLinkLocale>.
        </p>
        <p>Укажите идентификатор вашего согласия и дату, когда вы связались с нами по поводу вашего согласия.</p>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Необходимые (8)</h3>
            <p>
                Необходимые файлы cookie помогают сделать веб-сайт пригодным для использования, обеспечивая базовые функции, такие как навигация по
                страницам и доступ к защищенным разделам сайта. Веб-сайт не может правильно функционировать без этих файлов cookie.
            </p>
            <ContentCookieTable :items="cookies.necessary" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Предпочтения (1)</h3>
            <p>
                Файлы cookie предпочтений позволяют веб-сайту запоминать информацию, которая изменяет поведение или внешний вид сайта, например ваш
                предпочтительный язык или регион, в котором вы находитесь.
            </p>
            <ContentCookieTable :items="cookies.preferences" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Маркетинг (2)</h3>
            <p>
                Маркетинговые файлы cookie используются для отслеживания посетителей на веб-сайтах. Цель состоит в том, чтобы показывать рекламу,
                которая актуальна и привлекательна для отдельного пользователя и, следовательно, более ценна для издателей и сторонних рекламодателей.
            </p>
            <ContentCookieTable :items="cookies.marketing" />
        </div>

        <div class="my-2 flex flex-col gap-4 border border-black p-4">
            <h3 class="text-highlighted font-bold">Неклассифицированные (1)</h3>
            <p>
                Неклассифицированные файлы cookie — это файлы cookie, которые находятся в процессе классификации вместе с поставщиками отдельных
                файлов cookie.
            </p>
            <ContentCookieTable :items="cookies.unclassified" />
        </div>
    </ContentPage>
</template>
